import React from 'react';
import { FileText, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import unknownLogo from '../assets/unknown_logo.png';

interface PaidReportProps {
  company_logo_url?: string;
  company_name: string;
  id: string;
  payment_date?: string;
  onViewDetails: () => void;
  isTestReport?: boolean;
}

const PaidReportCard: React.FC<PaidReportProps> = ({
  company_logo_url,
  company_name,
  payment_date,
  onViewDetails,
  isTestReport = false,
}) => {
  const { t } = useTranslation();

  const formattedDate = payment_date
    ? new Date(payment_date).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : t('paidReportCard.noDate');

  return (
    <div
      className="
        relative flex flex-col min-h-[420px] overflow-hidden
        rounded-2xl border border-emerald-100
        bg-gradient-to-b from-emerald-50 to-white
        shadow-[0_8px_24px_rgba(15,23,42,0.04)]
        transition-all duration-300
        hover:shadow-[0_12px_36px_rgba(15,23,42,0.10)]
        hover:-translate-y-0.5
      "
    >
      {/* badge de test */}
      {isTestReport && (
        <div className="absolute top-4 right-4 z-10 group">
          <Info className="w-6 h-6 text-yellow-500 animate-pulse cursor-pointer drop-shadow-lg" />
          <div
            className="
              invisible opacity-0 group-hover:visible group-hover:opacity-100
              absolute right-0 mt-2 w-56 rounded-md bg-gray-900 px-3 py-2 text-xs text-white
              shadow-lg transition-all duration-200
            "
          >
            {t('paidReportCard.testReportTooltip', 'Informe de test: los datos no son reales.')}
          </div>
        </div>
      )}

      {/* cabecera */}
      <div className="p-6 flex flex-col items-center text-center">
        <div className="flex h-28 w-28 items-center justify-center overflow-hidden rounded-2xl bg-white ring-1 ring-emerald-200 shadow-sm">
          <img
            src={company_logo_url || unknownLogo}
            alt={company_name}
            className="h-full w-full object-contain p-2"
          />
        </div>
        <h3 className="mt-4 font-bold text-2xl text-slate-900">{company_name}</h3>

        {isTestReport ? (
          <p className="mt-1 text-sm font-semibold text-yellow-600">
            {t('paidReportCard.testReportLabel')}
          </p>
        ) : (
          <span className="mt-2 inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-800 shadow-sm">
            ● {t('paidReportCard.purchaseDate', { date: formattedDate })}
          </span>
        )}
      </div>

      {/* cuerpo */}
      <div className="px-6 flex-1 flex flex-col justify-center">
        <p className="text-base text-slate-600 mb-4 text-justify">
          {t('paidReportCard.description', { company_name })}
        </p>
      </div>

      {/* footer */}
      <div className="p-6 pt-0">
        <button
          className="
            w-full inline-flex items-center justify-center gap-2 rounded-xl
            bg-gradient-to-r from-emerald-500 to-teal-600 px-5 py-4
            text-lg font-semibold text-white shadow-sm
            transition hover:from-emerald-600 hover:to-teal-700 active:scale-[0.99]
          "
          onClick={onViewDetails}
        >
          <FileText className="h-6 w-6" />
          {t('paidReportCard.viewReport')}
        </button>
      </div>
    </div>
  );
};

export default PaidReportCard;
